import React from 'react'
import * as R from 'ramda'
import { t } from 'ttag'

import {
  CATEGORY_PLACEMENT,
  INSERTED_CATEGORY,
  MODES,
  STATIC_PAGES,
  getNewCategory,
} from '../model'

// NOTE: hook that handles state and logic to maintain
// steps transition, switching mode from read only to edit,
// adding new category, adding questions etc. and exposes
// state and handlers for other local hooks and the
// interview template creator components
export const useStepsState = (state, handlers) => {
  const { sections, skills } = state
  const { handleSetResponse } = handlers

  const [activeStep, setActiveStep] = React.useState(null)
  const [baseStep, setBaseStep] = React.useState(null)
  const [mode, setMode] = React.useState(MODES.IDLE)

  const scoredSectionsCount = R.count(({ scoreable }) => scoreable, sections)
  const sectionsCount = R.count(
    ({ key }) => !R.equals(key, INSERTED_CATEGORY),
    sections,
  )
  const questionsCount = R.pipe(
    R.filter(({ key }) => !R.includes(key, STATIC_PAGES)),
    R.reduce((acc, section) => acc + section.questions?.length, 0),
  )(sections)

  const competenciesCount = Object.values(skills).filter(val => val).length

  const isEditMode = R.equals(mode, MODES.EDIT)
  const isIdleMode = R.equals(mode, MODES.IDLE)

  const activeSection = React.useMemo(
    () =>
      R.find(
        section => R.equals(section?.key, activeStep) && section,
        sections,
      ) ?? {},
    [activeStep, sections],
  )

  const steps = React.useMemo(
    () =>
      R.map(({ key, scoreable, title }) => {
        const isActive = R.equals(activeStep, key)
        const isDisabled = isActive ? false : isEditMode ? true : false
        const isEdited = isEditMode

        return {
          key,
          label: title || t`New category`,
          scoreable,
          isActive,
          isDisabled,
          isEdited,
        }
      }, sections),
    [activeStep, sections, isEditMode],
  )

  const currentStepIndex = React.useMemo(
    () => R.findIndex(R.propEq('key', activeStep))(steps),
    [activeStep, steps],
  )

  const isNextStepDisabled = R.equals(activeStep, R.last(steps)?.key)
  const isPreviousStepDisabled = R.equals(activeStep, steps[0]?.key)
  const isStaticPage = R.includes(activeStep, STATIC_PAGES)

  const hasNoScoreableCategory = R.lte(scoredSectionsCount, 1)
  const isActiveCategoryScorable = R.equals(activeSection?.scoreable, R.T())
  const isInsertedCategory = R.equals(activeSection?.key, INSERTED_CATEGORY)
  const isCategoryScorable = !R.and(
    hasNoScoreableCategory,
    isActiveCategoryScorable,
  )
  const isCategoryDeletable = R.and(isCategoryScorable, !isInsertedCategory)
  const isNextCategoryDisabled = R.and(isNextStepDisabled, isStaticPage)
  const isPreviousCategoryDisabled = R.and(isPreviousStepDisabled, isStaticPage)

  const handleChangeStep = React.useCallback(
    step => {
      setActiveStep(step)
    },
    [setActiveStep],
  )

  const handleChangeBaseStep = React.useCallback(
    step => {
      setBaseStep(step)
    },
    [setBaseStep],
  )

  const handleChangeNextStep = React.useCallback(() => {
    if (R.gte(currentStepIndex, R.length(steps) - 1)) return

    const nextStep = steps[currentStepIndex + 1]?.key

    handleChangeStep(nextStep)
  }, [currentStepIndex, steps, handleChangeStep])

  const handleChangePreviousStep = React.useCallback(() => {
    if (R.lte(currentStepIndex, 0)) return

    const previousStep = steps[currentStepIndex - 1]?.key

    handleChangeStep(previousStep)
  }, [currentStepIndex, steps, handleChangeStep])

  const handleChangeMode = React.useCallback(() => {
    setMode(prevState =>
      R.equals(prevState, MODES.IDLE) ? MODES.EDIT : MODES.IDLE,
    )
  }, [setMode])

  const handleInsertCategory = React.useCallback(
    ({ type }) => () => {
      const index = R.equals(type, CATEGORY_PLACEMENT.NEXT)
        ? currentStepIndex + 1
        : currentStepIndex

      handleSetResponse(prevState => {
        handleChangeStep(INSERTED_CATEGORY)
        handleChangeBaseStep(activeStep)

        return R.over(
          R.lensPath(['sections']),
          R.insert(index, getNewCategory()),
          prevState,
        )
      })
      handleChangeMode()
    },
    [
      activeStep,
      currentStepIndex,
      handleChangeBaseStep,
      handleChangeMode,
      handleChangeStep,
      handleSetResponse,
    ],
  )

  const handleInsertCategoryCancel = React.useCallback(() => {
    handleSetResponse(prevState => {
      handleChangeStep(baseStep)
      handleChangeBaseStep(null)

      return R.over(
        R.lensPath(['sections']),
        R.remove(currentStepIndex, 1),
        prevState,
      )
    })
    handleChangeMode()
  }, [
    baseStep,
    currentStepIndex,
    handleChangeBaseStep,
    handleChangeMode,
    handleChangeStep,
    handleSetResponse,
  ])

  React.useEffect(() => {
    if (R.isNil(steps) || R.isEmpty(steps) || !R.isNil(activeStep)) return

    handleChangeStep(steps[0]?.key)
  }, [activeStep, steps, handleChangeStep])

  return {
    state: {
      activeSection,
      activeStep,
      currentStepIndex,
      questionsCount,
      sectionsCount,
      competenciesCount,
      steps,
      isCategoryScorable,
      isCategoryDeletable,
      isEditMode,
      isIdleMode,
      isInsertedCategory,
      isNextCategoryDisabled,
      isNextStepDisabled,
      isPreviousCategoryDisabled,
      isPreviousStepDisabled,
      isStaticPage,
    },
    handlers: {
      handleChangeNextStep,
      handleChangePreviousStep,
      handleChangeMode,
      handleChangeStep,
      handleInsertCategory,
      handleInsertCategoryCancel,
    },
  }
}
